import { Heightmap } from '../world/heightmap'

/**
 * Mreža kula i vidljivost između njih. Namerno bez three.js — ovo je isti
 * sloj koji će kasnije da živi i u Durable Objectu, gde nema ni scene ni
 * kamere, samo brojevi.
 */

export const BROJ_IGRACA = 4

export interface Nivo {
  /** Visina stuba iznad terena, u svetskim jedinicama na mapi od 1400. */
  visina: number
  /** Osnovni domet, pre bonusa za nadmorsku visinu. */
  domet: number
  ime: string
}

export const NIVOI: Nivo[] = [
  { ime: 'niska', visina: 20, domet: 165 },
  { ime: 'srednja', visina: 32, domet: 265 },
  { ime: 'visoka', visina: 46, domet: 380 },
]

export interface Kula {
  id: number
  igrac: number
  nivo: number
  x: number
  z: number
  /** Visina terena ispod kule. */
  teren: number
  /** Visina vrha kule — odatle se gleda. */
  vrh: number
  domet: number
}

export interface Veza {
  a: number
  b: number
  duzina: number
  vidi: boolean
  /** Prva tačka u kojoj teren preseca liniju pogleda, ako je preseca. */
  prepreka?: { x: number; y: number; z: number }
}

export interface Predlog {
  moze: boolean
  razlog?: string
  teren: number
  vrh: number
  domet: number
}

/** Najmanje rastojanje između dve kule, na mapi od 1400. */
const MIN_RAZMAK = 34
/** Koliko se klik sme pomeriti da uhvati greben, na mapi od 1400. */
const PRIVLACENJE = 15
/** Iznad ovog nagiba nema ravnog mesta za kulu. */
const MAX_NAGIB = 0.68
/** Koliko nadmorska visina produžava domet. */
const BONUS_VISINE = 0.55
/** Tolerancija da linija koja gleda tik iznad grebena ne pada na zaokruživanju. */
const ZAZOR = 1.2

export class Mreza {
  kule: Kula[] = []
  veze: Veza[] = []
  private sledeciId = 1
  private skala: number

  constructor(private hm: Heightmap) {
    this.skala = hm.size / 1400
  }

  private visinaNivoa(nivo: number): number {
    return NIVOI[nivo].visina * this.skala
  }

  private dometNivoa(nivo: number, teren: number): number {
    return (NIVOI[nivo].domet + teren * BONUS_VISINE) * this.skala
  }

  /** Da li i zašto se na ovoj tački može graditi. */
  proveri(x: number, z: number, nivo: number): Predlog {
    const teren = this.hm.sample(x, z)
    const vrh = teren + this.visinaNivoa(nivo)
    const domet = this.dometNivoa(nivo, teren)
    const van = this.hm.size / 2 - this.hm.cell * 2

    if (Math.abs(x) > van || Math.abs(z) > van) {
      return { moze: false, razlog: 'van mape', teren, vrh, domet }
    }
    if (teren < 2) {
      return { moze: false, razlog: 'u vodi', teren, vrh, domet }
    }
    if (this.nagibNa(x, z) > MAX_NAGIB) {
      return { moze: false, razlog: 'prestrmo', teren, vrh, domet }
    }
    const min = MIN_RAZMAK * this.skala
    for (const k of this.kule) {
      if ((k.x - x) ** 2 + (k.z - z) ** 2 < min * min) {
        return { moze: false, razlog: 'preblizu druge kule', teren, vrh, domet }
      }
    }
    return { moze: true, teren, vrh, domet }
  }

  /**
   * Klik retko pogodi tačno vrh. Tražimo najvišu ispravnu tačku u malom krugu
   * oko klika — kula sama „skoči" na greben, što je i mesto na koje bi je
   * čovek stvarno stavio.
   */
  odrediMesto(x: number, z: number, nivo: number): { x: number; z: number; predlog: Predlog } {
    const tacan = this.proveri(x, z, nivo)
    const r = PRIVLACENJE * this.skala

    let najx = x
    let najz = z
    let naj = tacan
    let najTeren = tacan.moze ? tacan.teren : -Infinity

    for (let prsten = 1; prsten <= 3; prsten++) {
      const rr = (r * prsten) / 3
      const uzoraka = prsten * 6
      for (let a = 0; a < uzoraka; a++) {
        const ugao = (a / uzoraka) * Math.PI * 2 + prsten
        const nx = x + Math.cos(ugao) * rr
        const nz = z + Math.sin(ugao) * rr
        const p = this.proveri(nx, nz, nivo)
        if (!p.moze) continue
        if (p.teren > najTeren) {
          najTeren = p.teren
          najx = nx
          najz = nz
          naj = p
        }
      }
    }
    return { x: najx, z: najz, predlog: naj }
  }

  dodaj(x: number, z: number, igrac: number, nivo: number): Kula | null {
    const mesto = this.odrediMesto(x, z, nivo)
    const p = mesto.predlog
    if (!p.moze) return null
    const kula: Kula = {
      id: this.sledeciId++,
      igrac,
      nivo,
      x: mesto.x,
      z: mesto.z,
      teren: p.teren,
      vrh: p.vrh,
      domet: p.domet,
    }
    this.kule.push(kula)
    this.preracunaj()
    return kula
  }

  ukloni(id: number): boolean {
    const i = this.kule.findIndex((k) => k.id === id)
    if (i < 0) return false
    this.kule.splice(i, 1)
    this.preracunaj()
    return true
  }

  ocisti(): void {
    this.kule = []
    this.veze = []
  }

  /**
   * Sve veze se računaju iznova. Sa četrdesetak kula to je ispod milisekunde,
   * a alternativa — inkrementalno održavanje — je izvor grešaka bez potrebe.
   */
  preracunaj(): void {
    this.veze = []
    for (let i = 0; i < this.kule.length; i++) {
      for (let j = i + 1; j < this.kule.length; j++) {
        const a = this.kule[i]
        const b = this.kule[j]
        const d = Math.hypot(b.x - a.x, b.z - a.z)
        // Domet je onaj kraći od dva — signal mora da stigne u oba smera.
        if (d > Math.min(a.domet, b.domet)) continue
        const v = vidljivost(this.hm, a.x, a.vrh, a.z, b.x, b.vrh, b.z)
        this.veze.push({
          a: a.id,
          b: b.id,
          duzina: d,
          vidi: v.vidi,
          prepreka: v.vidi ? undefined : v.tacka,
        })
      }
    }
  }

  /**
   * Vidljive veze predložene kule sa postojećima — za pregled pod kursorom.
   * Računa se za mesto na koje bi kula stvarno stala, ne za sam klik, da
   * pregled ne bi lagao.
   */
  pregled(x: number, z: number, nivo: number): {
    x: number
    z: number
    predlog: Predlog
    vidi: Kula[]
    nevidi: Kula[]
  } {
    const mesto = this.odrediMesto(x, z, nivo)
    const predlog = mesto.predlog
    const vidi: Kula[] = []
    const nevidi: Kula[] = []
    if (predlog.moze) {
      for (const k of this.kule) {
        const d = Math.hypot(k.x - mesto.x, k.z - mesto.z)
        if (d > Math.min(k.domet, predlog.domet)) continue
        const v = vidljivost(this.hm, mesto.x, predlog.vrh, mesto.z, k.x, k.vrh, k.z)
        if (v.vidi) vidi.push(k)
        else nevidi.push(k)
      }
    }
    return { x: mesto.x, z: mesto.z, predlog, vidi, nevidi }
  }

  /**
   * Najduži lanac jednog igrača: broj kula i ukupna dužina.
   * Lanac ide samo kroz vidljive veze i samo kroz sopstvene kule.
   */
  najduziLanac(igrac: number): { kula: number; duzina: number } {
    const svoje = this.kule.filter((k) => k.igrac === igrac)
    if (!svoje.length) return { kula: 0, duzina: 0 }

    const susedi = new Map<number, { id: number; duzina: number }[]>()
    for (const k of svoje) susedi.set(k.id, [])
    for (const v of this.veze) {
      if (!v.vidi) continue
      const la = susedi.get(v.a)
      const lb = susedi.get(v.b)
      if (!la || !lb) continue
      la.push({ id: v.b, duzina: v.duzina })
      lb.push({ id: v.a, duzina: v.duzina })
    }

    // Najduži prost put u malom grafu — pretraga u dubinu sa vraćanjem.
    // Kula je do četrdesetak, pa je ovo brže nego bilo šta pametnije.
    let najboljeKula = 1
    let najboljaDuzina = 0
    const posecen = new Set<number>()
    // Traženje najdužeg puta je u principu eksponencijalno. Gusta mreža od
    // dvadeset kula bi ga oborila, pa pretraga ima budžet i vraća najbolje
    // što je stigla da nađe. Za igračku je to sasvim dovoljno tačno.
    let budzet = 120000

    const idi = (id: number, brojKula: number, duzina: number): void => {
      if (budzet-- <= 0) return
      if (brojKula > najboljeKula || (brojKula === najboljeKula && duzina > najboljaDuzina)) {
        najboljeKula = brojKula
        najboljaDuzina = duzina
      }
      for (const s of susedi.get(id) ?? []) {
        if (posecen.has(s.id)) continue
        posecen.add(s.id)
        idi(s.id, brojKula + 1, duzina + s.duzina)
        posecen.delete(s.id)
      }
    }

    for (const k of svoje) {
      posecen.clear()
      posecen.add(k.id)
      idi(k.id, 1, 0)
    }
    return { kula: najboljeKula, duzina: najboljaDuzina }
  }

  brojVeza(igrac: number): number {
    const moje = new Set(this.kule.filter((k) => k.igrac === igrac).map((k) => k.id))
    let n = 0
    for (const v of this.veze) if (v.vidi && moje.has(v.a) && moje.has(v.b)) n++
    return n
  }

  poId(id: number): Kula | undefined {
    return this.kule.find((k) => k.id === id)
  }

  /** Nagib se meri preko tri ćelije — jedna ćelija hvata šum, ne padinu. */
  private nagibNa(x: number, z: number): number {
    const d = this.hm.cell * 3
    const dx = (this.hm.sample(x + d, z) - this.hm.sample(x - d, z)) / (2 * d)
    const dz = (this.hm.sample(x, z + d) - this.hm.sample(x, z - d)) / (2 * d)
    const n = Math.hypot(dx, dz)
    return n / (1 + n)
  }
}

/**
 * Da li se dve tačke vide preko terena. Ovo je jedno pravilo cele igre —
 * marširamo po duži i gledamo da li teren negde probije liniju pogleda.
 */
export function vidljivost(
  hm: Heightmap,
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
): { vidi: boolean; tacka?: { x: number; y: number; z: number } } {
  const duzina = Math.hypot(bx - ax, bz - az)
  const koraka = Math.max(8, Math.ceil(duzina / (hm.cell * 0.85)))

  for (let i = 1; i < koraka; i++) {
    const t = i / koraka
    const x = ax + (bx - ax) * t
    const z = az + (bz - az) * t
    const linija = ay + (by - ay) * t
    const teren = hm.sample(x, z)
    if (teren > linija + ZAZOR) {
      return { vidi: false, tacka: { x, y: teren, z } }
    }
  }
  return { vidi: true }
}
